import {
  ArchiveBoxIcon,
  CalendarDaysIcon,
  ArrowUpOnSquareIcon,
  ChatBubbleBottomCenterTextIcon,
  ClipboardDocumentCheckIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../common/Button";

export type ExportAction = "draft" | "summary" | "leads" | "timeline" | "transcripts" | "discovery";

interface Props {
  hasFiles: boolean;
  // Which download is in flight, if any; that row shows as busy and the
  // rest stay clickable.
  busy: ExportAction | null;
  onAction: (action: ExportAction) => void;
}

// Every way to get something out of an investigation, in one place. Same
// DaisyUI focus-dropdown as the call detail menus: opens on focus, closes
// when focus leaves or an item blurs itself after firing.
const ExportMenu: React.FC<Props> = ({ hasFiles, busy, onAction }) => {
  const items: { key: ExportAction; label: string; hint: string; icon: React.ComponentType<{ className?: string }>; needsFiles: boolean }[] = [
    { key: "draft", label: "Draft Report", hint: "Write a case report from the findings", icon: DocumentTextIcon, needsFiles: true },
    { key: "summary", label: "Investigation summary", hint: "The current brief, PDF or Word", icon: DocumentArrowDownIcon, needsFiles: true },
    { key: "leads", label: "Lead sheet", hint: "Confirmed leads, PDF or Word", icon: ClipboardDocumentCheckIcon, needsFiles: true },
    { key: "timeline", label: "Timeline", hint: "The chronology, PDF or Word", icon: CalendarDaysIcon, needsFiles: true },
    { key: "transcripts", label: "Transcripts…", hint: "Choose calls; PDF, Word, or text", icon: ChatBubbleBottomCenterTextIcon, needsFiles: true },
  ];
  const fire = (key: ExportAction) => {
    (document.activeElement as HTMLElement | null)?.blur();
    onAction(key);
  };
  const row = (key: ExportAction, label: string, hint: string, Icon: React.ComponentType<{ className?: string }>, disabled: boolean, title?: string) => (
    <li key={key} className={cn(disabled && "disabled")}>
      <button
        type="button"
        disabled={disabled}
        title={title}
        onClick={() => fire(key)}
        className={cn("!items-start gap-2.5 py-2 hover:bg-gray-100 dark:hover:bg-base-200", disabled && "cursor-not-allowed")}
      >
        <Icon className={cn("size-4 mt-0.5 shrink-0", busy === key && "animate-pulse")} />
        <span className="flex flex-col items-start min-w-0">
          <span className="text-sm font-medium leading-tight">{busy === key ? `${label.replace(/…$/, "")}…` : label}</span>
          <span className="text-[11px] text-base-content/60 leading-tight">{hint}</span>
        </span>
      </button>
    </li>
  );

  return (
    <div className="dropdown dropdown-end">
      {/* Same trigger as the case editor's Export (EditorMenuBar): primary
          blue, upload-square icon, no caret. */}
      <Button tabIndex={0} variant="primary" size="sm" title="Export">
        <div className="flex items-center">
          <ArrowUpOnSquareIcon className="size-6 mr-1 hidden xl:inline-block" />
          <p>Export</p>
        </div>
      </Button>
      <ul tabIndex={0} className="dropdown-content menu menu-sm bg-base-100 rounded-lg z-[30] w-64 p-1.5 shadow border border-base-300 mt-1">
        {items.map((it) => row(it.key, it.label, it.hint, it.icon, it.needsFiles && !hasFiles, it.needsFiles && !hasFiles ? "Add files first" : undefined))}
        <li className="my-1 h-px bg-base-300" aria-hidden />
        {row("discovery", "Discovery package", "Summary, leads, timeline, transcripts, chat, audit log", ArchiveBoxIcon, false)}
      </ul>
    </div>
  );
};

export default ExportMenu;
